import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AddPickingItemInput,
  BulkPickInput,
  CreatePickingOrderInput,
  CreatePickingPackageInput,
  ItemResponse,
  ListResponse,
  PickPickingItemInput,
  PickingOrderListItem,
  PickingOrderResponse,
  PickingPackageResponse,
  PickingPhotoResponse,
  SetPickingItemPackageSplitsInput,
  SetPickingItemsPackageSplitsBulkInput,
  UpdatePickingItemInput,
  UpdatePickingPackageInput,
} from '@proerp/shared';
import { apiRequest, postAuthedFile } from '@/lib/apiClient';

const KEY = ['picking-orders'] as const;

/**
 * Kommissionieraufträge eines Projekts (oder alle, wenn projectId leer).
 *
 * `sort` ist bewusst ein Parameter und kein fester Wert: Die Modulliste sortiert
 * nach Liefertermin (VB-26013 — die Priorität des Lagers), im Projekt-Register
 * bleibt es bei „neueste zuerst", weil dort alle Aufträge denselben Termin haben.
 */
export function usePickingOrders(projectId?: string, sort = '-createdAt') {
  return useQuery({
    queryKey: [...KEY, { projectId, sort }],
    queryFn: () =>
      apiRequest<ListResponse<PickingOrderListItem>>(
        `/v1/picking-orders?page=1&pageSize=100&sort=${sort}${projectId ? `&projectId=${projectId}` : ''}`,
      ),
    placeholderData: keepPreviousData,
  });
}

/**
 * Kommissionierung(en), die einen Lieferschein ausgelöst haben (Belegfluss:
 * Kommissionierung → Warenausgang → Lieferschein). Für den Absprung vom
 * Lieferschein zurück zur auslösenden Kommissionierung.
 */
export function usePickingOrdersByDeliveryNote(deliveryNoteId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'by-delivery-note', deliveryNoteId],
    queryFn: () =>
      apiRequest<ListResponse<PickingOrderListItem>>(
        `/v1/picking-orders?deliveryNoteId=${deliveryNoteId ?? ''}`,
      ),
    enabled: Boolean(deliveryNoteId),
  });
}

export function usePickingOrder(id: string) {
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => apiRequest<ItemResponse<PickingOrderResponse>>(`/v1/picking-orders/${id}`),
    enabled: Boolean(id),
  });
}

/** Auftrag aus der Projekt-Materialliste erzeugen (Snapshot). */
export function useCreatePickingOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePickingOrderInput) =>
      apiRequest<ItemResponse<PickingOrderResponse>>('/v1/picking-orders', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

/** Auftrag löschen (Soft-Delete) — gesperrt sobald abgeschlossen/in Lieferschein gewandelt (Guard: Backend). */
export function useDeletePickingOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest<{ data: { id: string } }>(`/v1/picking-orders/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

/** Freigabe (Vier-Augen — Ersteller ≠ Freigeber). */
export function useReleasePickingOrder(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<ItemResponse<PickingOrderResponse>>(`/v1/picking-orders/${id}/release`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...KEY, 'detail', id] });
      queryClient.invalidateQueries({ queryKey: KEY });
    },
  });
}

/** Büro-/Projektleiter-Vermerk je Position (KW34, kürzen …). */
export function useUpdatePickingItem(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, input }: { itemId: string; input: UpdatePickingItemInput }) =>
      apiRequest<ItemResponse<PickingOrderResponse>>(`/v1/picking-orders/${id}/items/${itemId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...KEY, 'detail', id] }),
  });
}

/** Detail-Query nach einer Änderung neu laden (gemeinsame Invalidierung). */
function useInvalidateDetail(id: string) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [...KEY, 'detail', id] });
    queryClient.invalidateQueries({ queryKey: KEY });
  };
}

/** Snapshot der Detail-Query vor einem optimistischen Update — für den Rollback bei Fehlern. */
type DetailSnapshot = ItemResponse<PickingOrderResponse>;

/** P2: Kommissionierung starten (FREIGEGEBEN → IN_ARBEIT). */
export function useStartPicking(id: string) {
  const invalidate = useInvalidateDetail(id);
  return useMutation({
    mutationFn: () => apiRequest<ItemResponse<PickingOrderResponse>>(`/v1/picking-orders/${id}/start-picking`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

/** P2: Lageristen-Rückmeldung je Position (Ist-Menge, Vermerk, Packstück). */
export function usePickItem(id: string) {
  const invalidate = useInvalidateDetail(id);
  return useMutation({
    mutationFn: ({ itemId, input }: { itemId: string; input: PickPickingItemInput }) =>
      apiRequest<ItemResponse<PickingOrderResponse>>(`/v1/picking-orders/${id}/items/${itemId}/pick`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}

/** Zusätzliche Position während der Kommissionierung ergänzen (Lagerist). */
export function useAddPickingItem(id: string) {
  const invalidate = useInvalidateDetail(id);
  return useMutation({
    mutationFn: (input: AddPickingItemInput) =>
      apiRequest<ItemResponse<PickingOrderResponse>>(`/v1/picking-orders/${id}/items`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}

/**
 * P2: mehrere Positionen in einem Rutsch zurückmelden (kumuliertes Abhaken). Optimistisch
 * (Tech-Lead 2026-07-22, #Ladeperformance): ohne lokales Update fühlte sich jeder Klick auf
 * dem Tablet spürbar träge an — Häkchen/Menge/Packstück warteten auf den Server-Roundtrip
 * UND den anschließenden Refetch, bevor sich die Zeile änderte. Jetzt wird der Cache sofort
 * mit genau den gesendeten Feldern aktualisiert; ein Fehler macht den Snapshot rückgängig,
 * ein Refetch im Hintergrund (`onSettled`) gleicht danach mit dem Server ab.
 */
export function usePickItemsBulk(id: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateDetail(id);
  const detailKey = [...KEY, 'detail', id];
  return useMutation({
    mutationFn: (input: BulkPickInput) =>
      apiRequest<ItemResponse<PickingOrderResponse>>(`/v1/picking-orders/${id}/pick-bulk`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<DetailSnapshot>(detailKey);
      if (previous) {
        const updateByItemId = new Map(input.items.map((update) => [update.itemId, update]));
        queryClient.setQueryData<DetailSnapshot>(detailKey, {
          ...previous,
          data: {
            ...previous.data,
            items: previous.data.items.map((item) => {
              const update = updateByItemId.get(item.id);
              if (!update) return item;
              return {
                ...item,
                ...(update.pickedQty !== undefined ? { pickedQty: update.pickedQty } : {}),
                ...(update.warehouseNote !== undefined ? { warehouseNote: update.warehouseNote } : {}),
                ...(update.packageId !== undefined ? { packageId: update.packageId } : {}),
              };
            }),
          },
        });
      }
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous);
    },
    onSettled: invalidate,
  });
}

/**
 * Aufteilung einer Position auf mehrere Packstücke setzen (Tech-Lead 2026-07-22) —
 * ersetzt komplett, was vorher für diese Position gespeichert war. Optimistisch aus
 * demselben Grund wie {@link usePickItemsBulk} (Ladeperformance am Tablet).
 */
export function useSetPickingItemPackageSplits(id: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateDetail(id);
  const detailKey = [...KEY, 'detail', id];
  return useMutation({
    mutationFn: ({ itemId, input }: { itemId: string; input: SetPickingItemPackageSplitsInput }) =>
      apiRequest<ItemResponse<PickingOrderResponse>>(`/v1/picking-orders/${id}/items/${itemId}/package-splits`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onMutate: async ({ itemId, input }) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<DetailSnapshot>(detailKey);
      if (previous) {
        queryClient.setQueryData<DetailSnapshot>(detailKey, {
          ...previous,
          data: {
            ...previous.data,
            items: previous.data.items.map((item) => (item.id === itemId ? { ...item, packageSplits: input.splits } : item)),
          },
        });
      }
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous);
    },
    onSettled: invalidate,
  });
}

/**
 * Aufteilung mehrerer Positionen in EINEM Aufruf setzen (kumulierter Modus, ADR 0061
 * erweitert): eine kumulierte Gruppe wird auf mehrere Collis verteilt, der Client bildet
 * das über {@link distributeGroupPackageSplits} auf die einzelnen Positionen ab und sendet
 * sie gebündelt. Ein Bulk-Call statt N Einzel-PATCHes — atomar (Backend: eine Transaktion),
 * ein optimistisches Cache-Update. Optimistisch aus demselben Grund wie
 * {@link usePickItemsBulk} (Ladeperformance am Tablet).
 */
export function useSetPickingItemsPackageSplitsBulk(id: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateDetail(id);
  const detailKey = [...KEY, 'detail', id];
  return useMutation({
    mutationFn: (input: SetPickingItemsPackageSplitsBulkInput) =>
      apiRequest<ItemResponse<PickingOrderResponse>>(`/v1/picking-orders/${id}/package-splits`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<DetailSnapshot>(detailKey);
      if (previous) {
        const splitsByItemId = new Map(input.items.map((entry) => [entry.itemId, entry.splits]));
        queryClient.setQueryData<DetailSnapshot>(detailKey, {
          ...previous,
          data: {
            ...previous.data,
            items: previous.data.items.map((item) => {
              const splits = splitsByItemId.get(item.id);
              return splits ? { ...item, packageSplits: splits } : item;
            }),
          },
        });
      }
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous);
    },
    onSettled: invalidate,
  });
}

/** P3: Packstück anlegen. */
export function useCreatePackage(id: string) {
  const invalidate = useInvalidateDetail(id);
  return useMutation({
    mutationFn: (input: CreatePickingPackageInput) =>
      apiRequest<ItemResponse<PickingPackageResponse>>(`/v1/picking-orders/${id}/packages`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}

/** P3: Packstück ändern (Maße/Gewichte/Notiz). */
export function useUpdatePackage(id: string) {
  const invalidate = useInvalidateDetail(id);
  return useMutation({
    mutationFn: ({ packageId, input }: { packageId: string; input: UpdatePickingPackageInput }) =>
      apiRequest<ItemResponse<PickingPackageResponse>>(`/v1/picking-orders/${id}/packages/${packageId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}

/** P3: Packstück löschen. */
export function useDeletePackage(id: string) {
  const invalidate = useInvalidateDetail(id);
  return useMutation({
    mutationFn: (packageId: string) =>
      apiRequest<{ data: { id: string } }>(`/v1/picking-orders/${id}/packages/${packageId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/** P3.5: Foto hochladen (Rohdaten im Body, Metadaten als Query). */
export function useUploadPhoto(id: string) {
  const invalidate = useInvalidateDetail(id);
  return useMutation({
    mutationFn: ({ file, packageId, pickingItemId, note }: { file: File; packageId?: string | null; pickingItemId?: string | null; note?: string | null }) => {
      const params = new URLSearchParams({ fileName: file.name });
      if (packageId) params.set('packageId', packageId);
      if (pickingItemId) params.set('pickingItemId', pickingItemId);
      if (note) params.set('note', note);
      return postAuthedFile<ItemResponse<PickingPhotoResponse>>(`/v1/picking-orders/${id}/photos?${params.toString()}`, file);
    },
    onSuccess: invalidate,
  });
}

/**
 * P4: Kommissionierung abschließen (Lagerist) — IN_ARBEIT → KOMMISSIONIERT.
 * Bucht NICHT den Warenausgang mehr (ADR 0043) — das übernimmt
 * {@link useBookStockOut} (Admin/Sekretariat).
 */
export function useCompletePickingOrder(id: string) {
  const invalidate = useInvalidateDetail(id);
  return useMutation({
    mutationFn: () => apiRequest<ItemResponse<PickingOrderResponse>>(`/v1/picking-orders/${id}/complete`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

/**
 * P4b: Lagerausgang buchen (Admin/Sekretariat, ADR 0043) — KOMMISSIONIERT →
 * ABGESCHLOSSEN. Bucht den Warenausgang + löst Projekt-Reservierungen auf.
 */
export function useBookStockOut(id: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateDetail(id);
  return useMutation({
    mutationFn: () => apiRequest<ItemResponse<PickingOrderResponse>>(`/v1/picking-orders/${id}/book`, { method: 'POST' }),
    onSuccess: () => {
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['articles'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

/**
 * Kommissionierungs-Abschluss rückgängig (Lagerist) — KOMMISSIONIERT →
 * IN_ARBEIT. Keine Lagerbuchung betroffen (es wurde noch keine vorgenommen).
 */
export function useReopenPickingOrder(id: string) {
  const invalidate = useInvalidateDetail(id);
  return useMutation({
    mutationFn: () => apiRequest<ItemResponse<PickingOrderResponse>>(`/v1/picking-orders/${id}/reopen`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

/**
 * Lagerausgang-Buchung rückgängig (Admin/Sekretariat, ADR 0043) — ABGESCHLOSSEN
 * → KOMMISSIONIERT. Storniert den gebuchten Warenausgang.
 */
export function useReopenBooking(id: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateDetail(id);
  return useMutation({
    mutationFn: () => apiRequest<ItemResponse<PickingOrderResponse>>(`/v1/picking-orders/${id}/reopen-booking`, { method: 'POST' }),
    onSuccess: () => {
      invalidate();
      // Storno bucht den Bestand zurück → Artikel/Projekte neu laden.
      void queryClient.invalidateQueries({ queryKey: ['articles'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

/** Kommissionierbeleg in einen Lieferschein (Verkauf) wandeln. */
export function useConvertToDeliveryNote(id: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateDetail(id);
  return useMutation({
    mutationFn: () =>
      apiRequest<ItemResponse<{ id: string; number: string }>>(`/v1/picking-orders/${id}/convert-to-delivery-note`, { method: 'POST' }),
    onSuccess: () => {
      invalidate();
      // Der neue Lieferschein soll sofort in der Verkauf-Liste auftauchen.
      queryClient.invalidateQueries({ queryKey: ['sales-documents'] });
    },
  });
}
